import { useRef, useState, useMemo, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { randomboxApi } from '../../api/randombox.js';
import { Spinner, ErrorBox, Field, ProductCell, StatusBadge } from '../../components/ui.js';
import { GradeBadge } from '../../components/GradeBadge.js';
import { OrderStatusBadges } from './OrderStatusBadges.js';
import { text, money, num, datetime } from '../../lib/format.js';
import { useAddressSearch } from '../../lib/daumPostcode.js';
import { useAutoGrowTextarea } from '../../lib/hooks.js';
import type { RbOrder, RbOrderHistory, RbOrderItem, Shipping } from '../../api/types.js';

/**
 * 원본: randombox/order/randombox_order_detail.php + randombox_order_detail_update.php
 *
 * 원본은 저장 시 PDO 트랜잭션으로 배송지 upsert · 주문 갱신 · 부분취소 · 복구를 묶었다.
 * 여기서도 같은 원자성을 유지한다 - 서버가 db_proxy 의 트랜잭션 모드로 한 번에 보낸다.
 * 그래서 이 화면의 저장은 "전부 반영되거나 전부 취소되거나" 둘 중 하나다.
 */

type ItemState = Record<number, boolean>; // seq -> canceled 여부

/**
 * 폼의 처음 값. 저장 전 상태로 되돌릴 때도 같은 값이 필요하다.
 *
 * 예전에는 이 객체를 `useState` 초기값과 '되돌리기' 버튼 두 곳에 **똑같이 두 번**
 * 적어 두었다. 칸을 하나 더할 때 한쪽만 고치면 되돌리기가 그 칸을 놔두고
 * 지나가므로, 한 곳에서만 만든다.
 */
function initialForm(order: RbOrder, shipping: Shipping | null) {
  return {
    memberName: shipping?.name ?? '',
    memberPhone: shipping?.phone ?? '',
    memberZipcode: shipping?.zipcode ?? '',
    memberAddr1: shipping?.address1 ?? '',
    memberAddr2: shipping?.address2 ?? '',
    trackingNumber: order.trackingNumber ?? '',
    orderStatus: order.orderStatus ?? '',
    memo: order.memo ?? '',
  };
}

export function RbOrderDetailPage() {
  const { orderNo = '' } = useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['randombox', 'order', orderNo],
    queryFn: () => randomboxApi.order(orderNo),
    enabled: Boolean(orderNo),
  });

  // 서버 값이 도착하면 폼 상태를 그것으로 초기화한다.
  // key 로 orderNo 를 주기 때문에 주문이 바뀌면 컴포넌트가 새로 마운트된다.
  if (query.isPending) return <Spinner />;
  if (query.isError) return <ErrorBox error={query.error} onRetry={() => void query.refetch()} />;

  return <OrderEditor key={orderNo} orderNo={orderNo} data={query.data} queryClient={queryClient} />;
}

export type OrderDetailData = NonNullable<
  ReturnType<typeof randomboxApi.order> extends Promise<infer T> ? T : never
>;

/**
 * 목록에서 클릭했을 때는 OrderDetailModal 이, 주소를 직접 열었을 때는 이 파일의
 * RbOrderDetailPage(전체 화면)가 이 컴포넌트를 그대로 재사용한다 - onClose 유무로
 * "뒤로 가기 링크" 대 "닫기 버튼"만 갈린다.
 */
export function OrderEditor({
  orderNo,
  data,
  queryClient,
  onClose,
}: {
  orderNo: string;
  data: OrderDetailData;
  queryClient: ReturnType<typeof useQueryClient>;
  onClose?: () => void;
}) {
  const { order, shipping, items, totalQuantity, canceledCount } = data;
  /**
   * 처리 이력은 없을 수도 있다.
   *
   * 프론트가 서버보다 먼저 올라간 동안에는 응답에 `history` 가 아예 없다.
   * 기본값을 두지 않으면 주문 상세가 통째로 하얗게 된다 - 곁기록 때문에
   * 주문을 못 보는 일은 없어야 한다.
   */
  const history = data.history ?? [];

  const originalCanceled = useMemo<ItemState>(() => {
    const map: ItemState = {};
    for (const it of items) if (it.seq !== null) map[it.seq] = it.status === 'CANCELED';
    return map;
  }, [items]);

  const [form, setForm] = useState(() => initialForm(order, shipping));

  const [canceled, setCanceled] = useState<ItemState>(originalCanceled);

  const { cancelSeqs, restoreSeqs } = useMemo(() => {
    const cancel: number[] = [];
    const restore: number[] = [];
    for (const [seqStr, isCanceled] of Object.entries(canceled)) {
      const seq = Number(seqStr);
      const was = originalCanceled[seq] ?? false;
      if (isCanceled && !was) cancel.push(seq);
      if (!isCanceled && was) restore.push(seq);
    }
    return { cancelSeqs: cancel, restoreSeqs: restore };
  }, [canceled, originalCanceled]);

  // 칸을 하나하나 견주지 않고 처음 값과 통째로 견준다 - 칸을 더해도 여기를
  // 함께 고칠 일이 없다.
  const pristine = useMemo(() => initialForm(order, shipping), [order, shipping]);
  const formDirty = (Object.keys(pristine) as (keyof typeof pristine)[]).some(
    (key) => form[key] !== pristine[key],
  );

  const dirty = formDirty || cancelSeqs.length > 0 || restoreSeqs.length > 0;

  const mutation = useMutation({
    mutationFn: () => randomboxApi.updateOrder(orderNo, { ...form, cancelSeqs, restoreSeqs }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['randombox', 'order', orderNo] });
      await queryClient.invalidateQueries({ queryKey: ['randombox', 'orders'] });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    mutation.mutate();
  }

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const addressSearch = useAddressSearch();
  const addr2Ref = useRef<HTMLInputElement>(null);
  const memoRef = useAutoGrowTextarea(form.memo);

  function openAddressSearch() {
    addressSearch.open((addr) => {
      setForm((prev) => ({
        ...prev,
        memberZipcode: addr.zonecode,
        memberAddr1: addr.roadAddress || addr.jibunAddress,
      }));
      // 원본과 같이 주소를 고르면 상세주소 칸으로 바로 넘어간다.
      addr2Ref.current?.focus();
    });
  }

  const pendingCanceled = Object.values(canceled).filter(Boolean).length;

  return (
    <section className={onClose ? 'rb-order rb-order--drawer' : 'rb-order page--half-right'}>
      {/*
        머리.
        어떤 주문을 보고 있는지(주문번호)와 지금 상태가 가장 먼저 읽혀야 한다.
        패널에서는 이 머리가 위에 붙어 따라오므로, 경품이 스무 개인 주문을
        끝까지 내려도 무엇을 고치는 중인지 잃지 않는다.
      */}
      <header className="rb-order__head">
        <div className="rb-order__head-main">
          {onClose ? (
            <span className="rb-order__eyebrow">랜덤박스 주문 상세</span>
          ) : (
            <Link className="link link--back" to="/randombox/orders">
              ← 주문 목록
            </Link>
          )}
          <div className="rb-order__title-row">
            <h1 className="page-title rb-order__title">{text(order.orderNo)}</h1>
            <OrderStatusBadges order={order} canceledCount={canceledCount} showApplied />
            {order.isTest && <StatusBadge label="테스트 주문" tone="warn" />}
          </div>
          <p className="rb-order__meta">
            {text(order.product.name)}
            <span aria-hidden="true"> · </span>
            {datetime(order.orderDate)} 주문
          </p>
        </div>
        {onClose && (
          <button type="button" className="rb-order__close" onClick={onClose} aria-label="닫기" title="닫기 (Esc)">
            ×
          </button>
        )}
      </header>

      {/* 요약 한 줄 - 이 주문에서 가장 자주 확인하는 네 가지. */}
      <dl className="rb-order__summary">
        <div className="rb-order__stat rb-order__stat--main">
          <dt>총 결제금액</dt>
          <dd>{money(order.totalAmount)}</dd>
        </div>
        <div className="rb-order__stat">
          <dt>티켓</dt>
          <dd>{num(order.ticket)}</dd>
        </div>
        <div className="rb-order__stat">
          <dt>당첨 경품</dt>
          <dd>
            {items.length}건
            {canceledCount > 0 && <span className="rb-order__stat-sub rb-order__stat-sub--danger">취소 {canceledCount}</span>}
          </dd>
        </div>
        <div className="rb-order__stat">
          <dt>송장</dt>
          <dd>
            {order.trackingNumber ? (
              <code className="code">{order.trackingNumber}</code>
            ) : (
              <span className="td-muted">미등록</span>
            )}
          </dd>
        </div>
      </dl>

      {/*
        정보 묶음.
        주문 · 결제 · 주문자를 카드 세 장으로 흩지 않고 한 장 안에 세 단으로
        나란히 둔다 - 서로 견줘 보는 값들이라 눈이 한 번에 훑을 수 있어야 한다.
        저장된 메모는 아래 폼에서 고치지만, 처리 전에 먼저 읽어야 하는 내용이라
        여기에도 그대로 보여준다.
      */}
      <div className="rb-order__section">
        <div className="rb-order__info">
          <div className="rb-order__info-col">
            <h2 className="rb-order__label">주문</h2>
            <dl className="rb-order__dl">
              {/* 주문번호는 제목에 이미 크게 있다 - 여기 또 적지 않는다. */}
              <dt>주문일시</dt><dd>{datetime(order.orderDate)}</dd>
              <dt>랜덤박스</dt>
              <dd>
                {order.randomboxId ? (
                  <Link className="link" to={`/randombox/products/${order.randomboxId}/prizes`}>
                    {text(order.product.name)}
                  </Link>
                ) : (
                  text(order.product.name)
                )}
              </dd>
              <dt>티켓</dt><dd>{num(order.ticket)}</dd>
              <dt>주문 경로</dt><dd>{text(order.device)}</dd>
              {order.updatedAt && (
                <>
                  <dt>최종 수정</dt><dd>{datetime(order.updatedAt)}</dd>
                </>
              )}
            </dl>
          </div>

          <div className="rb-order__info-col">
            <h2 className="rb-order__label">결제</h2>
            <dl className="rb-order__dl rb-order__dl--num">
              <dt>상품금액</dt><dd>{money(order.unitAmount)}</dd>
              <dt>배송비</dt><dd>{money(order.deliveryPrice)}</dd>
              {/* 도서·산간 주문에만 붙는다 - 0 인 대부분의 주문에서는 줄을 아예 그리지 않는다. */}
              {order.surchargePrice ? (
                <>
                  <dt>지역별 추가배송료</dt><dd>{money(order.surchargePrice)}</dd>
                </>
              ) : null}
              <dt>총 결제금액</dt><dd className="rb-order__strong">{money(order.totalAmount)}</dd>
              {order.cardStatus && (
                <>
                  <dt>결제 상태</dt><dd>{order.cardStatus}</dd>
                </>
              )}
            </dl>
          </div>

          <div className="rb-order__info-col">
            <h2 className="rb-order__label">주문자</h2>
            <dl className="rb-order__dl">
              {/* 회원ID·이메일은 banana.shop_member 에만 있는 값이라 여기서는 볼 수
                  없다. 이 화면이 보는 randombox_member 에는 회원번호와 수취인 정보뿐이다. */}
              <dt>회원번호</dt><dd>{order.member.idx === null ? '-' : String(order.member.idx)}</dd>
              <dt>수취인명</dt><dd>{text(order.member.name)}</dd>
              <dt>누적 결제액</dt><dd>{money(shipping?.totalAmountPrice ?? null)}</dd>
            </dl>
          </div>
        </div>

        {order.memo && (
          <div className="rb-order__memo">
            <span className="rb-order__label">메모</span>
            <p>{order.memo}</p>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit}>
        <div className="rb-order__section">
          <h2 className="rb-order__section-title">
            당첨 경품
            <span className="card__count">
              {items.length}건 / 총 {totalQuantity}개 · 취소 {pendingCanceled}건
            </span>
          </h2>

          {items.length === 0 ? (
            <p className="td-muted rb-order__empty">기록된 경품이 없습니다.</p>
          ) : (
            <div className="table-wrap rb-order__table-wrap">
              <table className="table rb-order__items">
                <thead>
                  <tr>
                    <th>등급</th>
                    <th>경품명</th>
                    <th>바코드</th>
                    <th style={{ textAlign: 'right' }}>수량</th>
                    <th>상태</th>
                    <th>배송 취소</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: RbOrderItem, i: number) => {
                    const seq = item.seq;
                    const isCanceled = seq !== null ? (canceled[seq] ?? false) : false;
                    const changed = seq !== null && isCanceled !== (originalCanceled[seq] ?? false);
                    return (
                      <tr key={seq ?? i} className={changed ? 'row--changed' : undefined}>
                        <td className="rb-order__item-grade"><GradeBadge grade={item.grade} /></td>
                        <td className={isCanceled ? 'rb-order__item-name td-struck' : 'rb-order__item-name'}>
                          <ProductCell name={text(item.title)} thumbnail={item.thumbnail} />
                        </td>
                        <td className="rb-order__item-code"><code className="code">{text(item.barcode)}</code></td>
                        <td className="td-right rb-order__item-qty">{num(item.quantity)}</td>
                        <td className="td-nowrap rb-order__item-state">
                          <span className={isCanceled ? 'badge badge--danger' : 'badge'}>
                            {isCanceled ? 'CANCELED' : 'READY'}
                          </span>
                          {changed && <span className="rb-order__changed">변경됨</span>}
                        </td>
                        <td className="rb-order__item-cancel">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={isCanceled}
                              disabled={seq === null || mutation.isPending}
                              onChange={(e) => {
                                if (seq === null) return;
                                setCanceled((prev) => ({ ...prev, [seq]: e.target.checked }));
                              }}
                            />
                            <span>취소</span>
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rb-order__section">
          <h2 className="rb-order__section-title">배송 · 처리</h2>

          <div className="rb-order__form">
            <h3 className="rb-order__label">받는분</h3>
            <div className="form-grid">
              <Field label="받는분 이름 *">
                <input className="input" value={form.memberName} onChange={(e) => set('memberName')(e.target.value)} required maxLength={64} />
              </Field>
              <Field label="받는분 연락처 *">
                <input className="input" value={form.memberPhone} onChange={(e) => set('memberPhone')(e.target.value)} required maxLength={32} />
              </Field>
            </div>

            <h3 className="rb-order__label">배송지</h3>
            <div className="rb-order__address">
              <div className="address-row">
                <Field label="우편번호 *">
                  <input
                    className="input"
                    value={form.memberZipcode}
                    readOnly
                    placeholder="주소찾기로 입력"
                    required
                    maxLength={16}
                  />
                </Field>
                <button type="button" className="btn btn--ghost address-row__btn" onClick={openAddressSearch}>
                  {addressSearch.loading ? '불러오는 중…' : '주소찾기'}
                </button>
              </div>
              {/* 오류 상자는 화면 안에서 한 가지 모양만 쓴다 - 예전에는 이 자리만
                  경품 재고용 클래스(qty-msg)를 빌려 써서 모양이 달랐다. */}
              {addressSearch.error && <ErrorBox error={addressSearch.error} />}
              {/* 주소는 한 줄을 다 쓴다 - 반 칸에서는 도로명 뒤쪽(건물명)이 잘려 보였다. */}
              <Field label="주소 *">
                <input
                  className="input"
                  value={form.memberAddr1}
                  readOnly
                  placeholder="주소찾기로 입력"
                  required
                  maxLength={255}
                  title={form.memberAddr1}
                />
              </Field>
              <Field label="상세주소">
                <input
                  ref={addr2Ref}
                  className="input"
                  value={form.memberAddr2}
                  onChange={(e) => set('memberAddr2')(e.target.value)}
                  maxLength={255}
                />
              </Field>
            </div>

            <h3 className="rb-order__label">처리</h3>
            <div className="form-grid">
              <Field label="송장번호">
                <input className="input" value={form.trackingNumber} onChange={(e) => set('trackingNumber')(e.target.value)} maxLength={64} />
              </Field>
              <Field label="주문 상태">
                <select className="input" value={form.orderStatus} onChange={(e) => set('orderStatus')(e.target.value)}>
                  <option value="미배송">미배송</option>
                  <option value="배송완료">배송완료</option>
                  {/* 위 두 값 외의 기존 상태도 잃지 않도록 그대로 노출한다. */}
                  {form.orderStatus !== '미배송' && form.orderStatus !== '배송완료' && (
                    <option value={form.orderStatus}>{form.orderStatus || '(없음)'}</option>
                  )}
                </select>
              </Field>

              {/* 당첨 경품 표처럼 카드 폭을 전부 쓴다 - 한 줄짜리 input 으로는 메모가 잘려 보였다.
                  기본 2줄, 글이 길어지면 스크롤 대신 높이가 늘어난다(useAutoGrowTextarea). */}
              <Field label="메모" full>
                <textarea
                  ref={memoRef}
                  className="input input--area input--auto-grow"
                  rows={2}
                  value={form.memo}
                  onChange={(e) => set('memo')(e.target.value)}
                  maxLength={2000}
                />
              </Field>
            </div>
          </div>
        </div>

        <OrderHistoryCard history={history} />

        {/*
          저장 줄은 화면 아래에 붙어 따라온다.
          예전에는 배송 정보 카드 맨 끝에 있어서, 경품이 많은 주문에서 위쪽의
          취소 체크를 바꾼 뒤 저장하려면 한참 내려가야 했다. 무엇이 바뀌었는지도
          이 줄에서 바로 읽힌다.
        */}
        <div className="rb-order__actions">
          {mutation.isError && <ErrorBox error={mutation.error} />}
          <div className="rb-order__actions-row">
            <div className="rb-order__actions-msg">
              {dirty ? (
                <p className="rb-order__dirty">
                  저장하면 배송지 · 주문정보 · 배송취소가 <strong>한 트랜잭션으로 함께</strong> 반영됩니다.
                  하나라도 실패하면 전부 취소됩니다.
                  {cancelSeqs.length > 0 && ` 취소 예정 ${cancelSeqs.length}건.`}
                  {restoreSeqs.length > 0 && ` 복구 예정 ${restoreSeqs.length}건.`}
                </p>
              ) : mutation.isSuccess ? (
                <p className="ok-note">
                  저장되었습니다. (주문 {mutation.data.orderAffected}건 · 취소 {mutation.data.canceled}건 · 복구{' '}
                  {mutation.data.restored}건)
                </p>
              ) : (
                <p className="td-muted">변경한 내용이 없습니다.</p>
              )}
            </div>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={!dirty || mutation.isPending}
              onClick={() => {
                setForm(pristine);
                setCanceled(originalCanceled);
                mutation.reset();
              }}
            >
              되돌리기
            </button>
            <button className="btn btn--primary" type="submit" disabled={!dirty || mutation.isPending}>
              {mutation.isPending ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

/** 이력 종류를 화면 낱말로 바꾼다. 서버가 보내는 값은 코드다. */
const ACTION_LABELS: Record<string, { label: string; tone: 'neutral' | 'info' | 'danger' }> = {
  UPDATE: { label: '주문 저장', tone: 'neutral' },
  DELIVERY_APPLY: { label: '배송 반영', tone: 'info' },
  DELIVERY_STOP: { label: '배송 정지', tone: 'danger' },
};

/**
 * 관리자 처리 이력 (migrations/020).
 *
 * 예전에는 누가 무엇을 고쳤는지가 서버 로그 파일에만 남아 화면에서 볼 수
 * 없었다. 이력 표를 아직 만들지 않은 환경에서는 서버가 빈 배열을 보내므로,
 * 그때는 "왜 비어 있는지"를 적어 준다 - 빈 표만 보이면 기록이 안 되는 것인지
 * 고친 적이 없는 것인지 구분할 수 없다.
 */
function OrderHistoryCard({ history }: { history: RbOrderHistory[] }) {
  return (
    <div className="rb-order__section">
      <h2 className="rb-order__section-title">
        관리자 처리 이력
        <span className="card__count">{history.length}건</span>
      </h2>

      {history.length === 0 ? (
        <p className="td-muted rb-order__empty">
          기록된 처리가 없습니다. 이 주문을 저장하거나 배송 처리하면 여기에 남습니다.
          <br />
          <small>레거시 화면에서 고친 내용은 이력에 남지 않습니다.</small>
        </p>
      ) : (
        <div className="table-wrap rb-order__table-wrap">
          <table className="table rb-order__history">
            <thead>
              <tr>
                <th>시각</th>
                <th>처리</th>
                <th>담당자</th>
                <th>내용</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row, i) => {
                const action = ACTION_LABELS[row.action] ?? { label: row.action, tone: 'neutral' as const };
                return (
                  <tr key={row.seq ?? i}>
                    <td className="td-muted td-nowrap">{datetime(row.regdate)}</td>
                    <td><StatusBadge label={action.label} tone={action.tone} /></td>
                    <td>{text(row.actor)}</td>
                    <td className="order-history__summary">
                      {row.summary ? row.summary : <span className="td-muted">바뀐 칸 없음</span>}
                      {(row.canceledCount > 0 || row.restoredCount > 0) && (
                        <span className="td-sub">
                          {row.canceledCount > 0 && `배송 취소 ${row.canceledCount}건`}
                          {row.canceledCount > 0 && row.restoredCount > 0 && ' · '}
                          {row.restoredCount > 0 && `복구 ${row.restoredCount}건`}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
